/**
 * Properties list — buildings the tenant manages. Click a row to open
 * the detail page with the contracts linked to that property.
 *
 * Group scoping happens server-side: the active-group header narrows
 * the SQL filter. SWR re-keys on activeGroupId, so flipping the active
 * group invalidates this list automatically.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { tenantApi } from '../api.js';
import { useTenantSession } from '../auth-context.js';
import { ActiveGroupNotice, useActiveGroup } from '../components/GroupPicker.js';
import { useInvalidate, useTenantQuery } from '../query.js';
import { usePageTitle } from '../use-page-title.js';

/** Render the postal-code + ort line, skipping empty parts. */
function formatPostalLine(postalCode: string | null, ort: string | null): string {
  return [postalCode, ort].filter(Boolean).join(' ').trim();
}

export function PropertiesListPage() {
  usePageTitle('Fastigheter');
  const { token } = useTenantSession();
  const { activeGroupId } = useActiveGroup();
  const invalidate = useInvalidate();

  const { data, error, isLoading } = useTenantQuery(
    'properties.list',
    () => tenantApi.properties.list(),
  );
  const properties = data?.properties ?? null;

  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftStreet, setDraftStreet] = useState('');
  const [draftPostal, setDraftPostal] = useState('');
  const [draftOrt, setDraftOrt] = useState('');
  const [draftKommun, setDraftKommun] = useState('');
  const [busy, setBusy] = useState(false);
  const [writeErr, setWriteErr] = useState<string | null>(null);

  const resetDraft = () => {
    setDraftName('');
    setDraftStreet('');
    setDraftPostal('');
    setDraftOrt('');
    setDraftKommun('');
  };

  const onCreate = async () => {
    if (!token) return;
    if (!activeGroupId) {
      setWriteErr('Välj en grupp innan du skapar fastigheten.');
      return;
    }
    setBusy(true);
    setWriteErr(null);
    try {
      await tenantApi.properties.create({
        // group_id elided — the active-group header is authoritative.
        name: draftName.trim(),
        street: draftStreet.trim() || null,
        postal_code: draftPostal.trim() || null,
        ort: draftOrt.trim() || null,
        kommun: draftKommun.trim() || null,
      });
      resetDraft();
      setCreating(false);
      await invalidate('properties.list');
    } catch (e) {
      setWriteErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-stone-500">Fastigheter</h2>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white"
          >
            Ny fastighet
          </button>
        )}
      </div>
      {(writeErr || error) && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {writeErr ?? (error as Error).message}
        </div>
      )}
      {creating && (
        <div className="space-y-2 rounded border border-stone-200 bg-stone-50 p-3">
          <ActiveGroupNotice prefix="Skapas i" />
          <input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="Beteckning (t.ex. Klockaren 5)"
            className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
          />
          <input
            value={draftStreet}
            onChange={(e) => setDraftStreet(e.target.value)}
            placeholder="Adress, t.ex. Ångvägen 1-2"
            className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
          />
          <div className="grid grid-cols-[140px_1fr] gap-2">
            <input
              value={draftPostal}
              onChange={(e) => setDraftPostal(e.target.value)}
              placeholder="114 44"
              className="rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
            />
            <input
              value={draftOrt}
              onChange={(e) => setDraftOrt(e.target.value)}
              placeholder="Stockholm"
              className="rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
            />
          </div>
          <input
            value={draftKommun}
            onChange={(e) => setDraftKommun(e.target.value)}
            placeholder="Kommun, t.ex. Stockholm"
            className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                resetDraft();
              }}
              className="rounded px-3 py-1.5 text-xs font-medium text-stone-600"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={onCreate}
              disabled={busy || draftName.trim().length === 0}
              className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {busy ? 'Sparar…' : 'Skapa'}
            </button>
          </div>
        </div>
      )}
      {isLoading || properties === null ? (
        <div className="text-sm text-stone-400">loading…</div>
      ) : properties.length === 0 ? (
        <div className="rounded border border-dashed border-stone-300 p-6 text-center text-sm text-stone-500">
          Inga fastigheter ännu. Klicka "Ny fastighet" ovan.
        </div>
      ) : (
        <ul className="divide-y rounded border">
          {properties.map((p) => {
            const postalLine = formatPostalLine(p.postalCode, p.ort);
            return (
              <li key={p.id} className="hover:bg-stone-50">
                <Link to={`/properties/${p.id}`} className="block p-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-medium">{p.name}</div>
                    <span className="text-xs text-stone-500">
                      {p.contractCount} avtal
                    </span>
                  </div>
                  {(p.street || postalLine) && (
                    <div className="mt-1 text-xs text-stone-500">
                      {p.street}
                      {p.street && postalLine ? ' · ' : ''}
                      {postalLine}
                    </div>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
